import { useState, useEffect } from 'react';
import { feedbackService, FeedbackWithUser } from '../../../services/feedbackService';

const ITEMS_PER_PAGE = 10;

export function useAdminFeedbacksPage() {
  const [feedbacks, setFeedbacks] = useState<FeedbackWithUser[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackWithUser | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FeedbackWithUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  const getFilenameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url.startsWith('/api/') ? `${process.env.REACT_APP_API_URL || ''}${url}` : url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || 'image';
      return filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
    } catch {
      const parts = url.split('/');
      const filename = parts[parts.length - 1] || 'image';
      return filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
    }
  };

  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await feedbackService.getAllFeedbacks();
      setFeedbacks(data);
      setCurrentPage(1);
    } catch (err: any) {
      setError(err.message || 'Failed to load feedbacks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedbacks();
  }, []);

  const handleUpdateNotes = async () => {
    if (!selectedFeedback) return;
    try {
      setUpdating(true);
      await feedbackService.updateFeedbackAdmin(selectedFeedback.id, { admin_notes: adminNotes });
      await loadFeedbacks();
      setSelectedFeedback(null);
      setAdminNotes('');
    } catch (err: any) {
      alert(err.message || 'Failed to update notes');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteFeedback = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await feedbackService.deleteFeedbackAdmin(deleteTarget.id);
      await loadFeedbacks();
      setDeleteTarget(null);
    } catch (err: any) {
      alert(err.message || 'Failed to delete feedback');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(feedbacks.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedFeedbacks = feedbacks.slice(
    (safePage - 1) * ITEMS_PER_PAGE,
    safePage * ITEMS_PER_PAGE
  );

  return {
    feedbacks,
    paginatedFeedbacks,
    currentPage,
    setCurrentPage,
    totalPages,
    itemsPerPage: ITEMS_PER_PAGE,
    loading,
    error,
    selectedFeedback,
    setSelectedFeedback,
    adminNotes,
    setAdminNotes,
    updating,
    deleteTarget,
    setDeleteTarget,
    deleting,
    loadFeedbacks,
    getFilenameFromUrl,
    handleUpdateNotes,
    handleDeleteFeedback,
  };
}
